/**
 * +field: The Hollow Barrow — Mossvale's starter dungeon (docs/design/zones.md "Dungeons"), entered
 * from the barrow mound in Sunmeadow. Normal difficulty (keystone level 0) at mobLevel 4: no timer,
 * the run completes when both bosses are dead. timerMs / forcesRequired are sane values for an
 * optional keystone mode.
 *
 * Route (bots follow it): 2 ossuary pulls, 2 crypt pulls, the Bonecaller, a gallery pull, the King.
 */
import type { DungeonDef, NpcId, PackDef } from '@keystone/sim';
import { cluster } from '../dungeons/geometry';
import { BARROW_BOUNDS, BARROW_CORRIDORS, BARROW_DOORS, BARROW_ENTRANCE, BARROW_OBSTACLES, BARROW_PROPS, BARROW_ROOMS, type BarrowRoomId } from './barrowLayout';
import { BARROW_ENCOUNTER, MAP, MOB } from './ids';

const TRASH_AGGRO = 10;
const TRASH_SOCIAL = 8;
const BOSS_AGGRO = 12;

interface PackSpec {
  id: string;
  room: BarrowRoomId;
  at: [number, number];
  face: [number, number];
  mobs: NpcId[];
  encounterId?: string;
}

const { barrowGhoul: ghoul, barrowWisp: wisp, barrowCrawler: crawler } = MOB;

const PACKS: PackSpec[] = [
  // Ossuary: a ghoul swarm at the foot of the stair, then a crawler with its ghouls.
  { id: 'hb_p1', room: 'ossuary', at: [8, 46], face: [12, 34], mobs: [ghoul, ghoul, ghoul, ghoul] },
  { id: 'hb_p2', room: 'ossuary', at: [18, 58], face: [8, 46], mobs: [crawler, ghoul, ghoul] },
  // Lantern Crypt: the first casters (kick the Grave Bolts).
  { id: 'hb_p3', room: 'lantern_crypt', at: [60, 54], face: [48, 58], mobs: [wisp, ghoul, wisp, ghoul] },
  { id: 'hb_p4', room: 'lantern_crypt', at: [68, 66], face: [60, 54], mobs: [crawler, wisp, ghoul] },
  // Bonecaller's Shrine (mini-boss).
  { id: 'hb_bonecaller', room: 'bonecaller_shrine', at: [68, 106], face: [63, 92], mobs: [MOB.barrowBonecaller], encounterId: BARROW_ENCOUNTER.bonecaller },
  // Gallery of Bones: the King's guard at the tomb stair.
  { id: 'hb_p5', room: 'bone_gallery', at: [22, 102], face: [38, 103], mobs: [crawler, wisp, crawler] },
  // The Hollow King's tomb.
  { id: 'hb_king', room: 'kings_tomb', at: [27, 156], face: [26, 136], mobs: [MOB.barrowKing], encounterId: BARROW_ENCOUNTER.king },
];

function toPackDef(p: PackSpec): PackDef {
  const center = { x: p.at[0], y: p.at[1] };
  const def: PackDef = {
    id: p.id,
    roomId: p.room,
    spawns: cluster(center, p.mobs, { x: p.face[0], y: p.face[1] }, p.mobs.length > 3 ? 3.2 : 2.8),
    aggroRadius: p.encounterId ? BOSS_AGGRO : TRASH_AGGRO,
    socialRadius: TRASH_SOCIAL,
    link: 'linked',
  };
  if (p.encounterId) def.encounterId = p.encounterId;
  return def;
}

export const HOLLOW_BARROW_ROUTE = PACKS.map((p) => p.id);

export const hollowBarrow: DungeonDef = {
  id: MAP.hollowBarrow,
  name: 'The Hollow Barrow',
  // Keystone mode only (normal difficulty has no timer): ~1.6× a bot party's normal clear.
  timerMs: 12 * 60 * 1000,
  forcesRequired: 48,
  entrance: BARROW_ENTRANCE,
  entranceFacing: Math.PI / 2,
  rooms: BARROW_ROOMS,
  corridors: BARROW_CORRIDORS,
  obstacles: BARROW_OBSTACLES,
  doors: BARROW_DOORS,
  props: BARROW_PROPS,
  packs: PACKS.map(toPackDef),
  route: HOLLOW_BARROW_ROUTE,
  requiredEncounters: [BARROW_ENCOUNTER.bonecaller, BARROW_ENCOUNTER.king],
  bounds: BARROW_BOUNDS,
  mobLevel: 4,
};
